import { WIDE_LEFT_PANEL_WIDTH } from '../../templates/dialog/consts';
import {
  ConfigurationCanvasLeftPanel,
  ConfigurationCanvasPanelContent,
  ConfigurationCanvasPanelHeader,
} from '../../../configuration-canvas-panel';
import { WithEnvConnectionErrorPanel } from '../../../canvas-pages/united-canvas/canvas-page/error-panel/EnvConnectionErrorPanel';
import { HORIZONTAL_PADDING } from '../../../configuration-canvas-panel/consts';
import { useUniversalSearch } from '../../../../documentation/universal-search/useUniversalSearch';
import { UniversalSearchInput } from '../../../../documentation/universal-search/UniversalSearchInput';
import { UniversalSearchContent } from '../../../../documentation/universal-search/UniversalSearchContent';
import { UniversalSearchProvider } from '../../../../documentation/universal-search/UniversalSearchContext';
import { CenteredCircularProgress } from '../../../../common/CenteredCircularProgress';
import { useSelector } from 'react-redux';
import { selectSaltoObjectTypes } from '../../../../../reducers/global/globalReducer';
import { selectLoader } from '../../../../../reducers/documentation/documentationReducer';
import { Stack } from '@mui/material';
import {
  PanelEnvSelector,
  panelEnvSelectorHack,
} from '../../../environments/connect-env/PanelEnvSelector';
import { useConfigurationCanvas } from '../../../configuration-canvas/public/useConfigurationCanvas';
import { SearchListItem, SearchType } from '../../../../documentation/universal-search/types';
import { isMetadataElementInCanvas } from './helper';
import { MetadataElementsSearchEmpty } from './MetadataElementsSearchEmpty';
import { AddToCanvasButton } from './AddToCanvasButton';
import { useAddToCanvas } from './useAddToCanvas';
import { DocumentationFiltersProvider } from '../../../../documentation/selected-object/filters/DocumentationFiltersContext';
import { UI_EVENTS } from '../../../../../services/events';

const MetadataElementsPanelContent = ({
  onClose,
  crmOrgId,
}: {
  onClose: () => void;
  crmOrgId: string;
}) => {
  const saltoObjectTypes = useSelector(selectSaltoObjectTypes(crmOrgId));
  const showLoader = useSelector(selectLoader(crmOrgId));
  const isLoadingObjects = !saltoObjectTypes?.length || showLoader;
  const { canvasFunnelMap } = useConfigurationCanvas();
  const onAddToCanvas = useAddToCanvas();

  const {
    onSearchKeydown,
    performSearchProgrammatically,
    onSearchChange,
    results,
    isLoadingResults,
    onClearButtonClick,
    searchTxt,
    tempSearchTxt,
    filterOptions,
    selectedFilterValue,
    onSelectedFilterItem,
    onListItemClick,
    searchTypeOptions,
    selectedSearchType,
    onSelectSearchTypeOption,
  } = useUniversalSearch({
    crmOrgId,
    onItemClick: async (props) => {
      if (!props) return;
      onAddToCanvas({
        element: {
          metadataId: props.metadataId,
          label: props.label ?? '',
          parentType: props.parentType,
          dependencyType: props.dependencyType,
        },
        crmOrgId,
      });
    },
    onClearClick: undefined,
    onBiEventSearchStarted: undefined,
    onBiEventSearchResultsPresented: undefined,
  });

  const renderRightElement = (item: SearchListItem, isHovered: boolean) => {
    if (!item.metadataId) return null;

    const isInCanvas = isMetadataElementInCanvas({
      crmOrgId,
      metadataId: item.metadataId,
      canvasFunnelMap,
    });

    return (
      <AddToCanvasButton
        isStaticView={false}
        isInCanvas={isInCanvas}
        isHovered={isHovered}
        element={item}
        crmOrgId={crmOrgId}
        biEventName={UI_EVENTS.canvasAddMetadataToCanvas}
      />
    );
  };

  const getItemStyling = (item: SearchListItem) => {
    if (!item.metadataId) return {};

    const isInCanvas = isMetadataElementInCanvas({
      crmOrgId,
      metadataId: item.metadataId,
      canvasFunnelMap,
    });
    if (isInCanvas) {
      return {
        cursor: 'default',
        '&:hover': { background: 'initial' },
      };
    }

    return {};
  };

  return (
    <ConfigurationCanvasLeftPanel width={WIDE_LEFT_PANEL_WIDTH} sx={panelEnvSelectorHack}>
      <WithEnvConnectionErrorPanel shouldWaitForInitialFetch={false}>
        <PanelEnvSelector />
        <ConfigurationCanvasPanelHeader keepHorizontalPadding onClose={onClose}>
          Metadata Elements
        </ConfigurationCanvasPanelHeader>

        <ConfigurationCanvasPanelContent
          sx={{ overflow: 'hidden', padding: `0 ${HORIZONTAL_PADDING}px` }}
        >
          <Stack sx={{ height: '100%' }}>
            {isLoadingObjects && <CenteredCircularProgress />}

            {!isLoadingObjects && (
              <>
                <UniversalSearchInput
                  onKeyDown={onSearchKeydown}
                  onChange={onSearchChange}
                  onClearButtonClick={onClearButtonClick}
                  searchTxt={searchTxt ?? ''}
                  tempSearchTxt={tempSearchTxt ?? ''}
                  selectedFilterValue={selectedFilterValue}
                  filterOptions={filterOptions}
                  onSelectedFilterItem={onSelectedFilterItem}
                  isUniversalSearchListOpen={true}
                  menuOptions={searchTypeOptions}
                  selectedMenuOption={selectedSearchType}
                  onSelectMenuOption={onSelectSearchTypeOption}
                  autoFocus
                />

                <UniversalSearchContent
                  onListItemClick={onListItemClick}
                  showLoader={false}
                  results={results}
                  isLoadingResults={isLoadingResults}
                  crmOrgId={crmOrgId}
                  selectedFilterValue={selectedFilterValue}
                  filterOptions={filterOptions}
                  onSelectedFilterItem={onSelectedFilterItem}
                  isExpandedMode={false}
                  renderRightElement={renderRightElement}
                  getItemStyling={getItemStyling}
                  emptyState={
                    <MetadataElementsSearchEmpty
                      onClick={(searchText) =>
                        performSearchProgrammatically(searchText, SearchType.apiNameAndLabel)
                      }
                    />
                  }
                  forceRenderIcons={true}
                />
              </>
            )}
          </Stack>
        </ConfigurationCanvasPanelContent>
      </WithEnvConnectionErrorPanel>
    </ConfigurationCanvasLeftPanel>
  );
};

export const MetadataElementsPanel = (props: { onClose: () => void; crmOrgId: string }) => (
  <UniversalSearchProvider>
    <DocumentationFiltersProvider>
      <MetadataElementsPanelContent {...props} />
    </DocumentationFiltersProvider>
  </UniversalSearchProvider>
);
